import { createFileRoute, Navigate, Outlet, redirect, useLocation } from '@tanstack/react-router';
import { AppShell } from '@/components/app-shell/app-shell';
import { LockedShell } from '@/components/app-shell/locked-shell';
import { Skeleton } from '@/components/ui/skeleton';
import { LockedScreen } from '@/features/billing/components/locked-screen';
import { ActingMembershipProvider } from '@/features/scheduling/acting-as';
import { isApiError } from '@/lib/api';
import { signInSearch } from '@/lib/home';
import { impersonationEndedRedirect } from '@/lib/impersonation-guard';
import { meQueryOptions, useMe } from '@/lib/me';
import { pickActiveMembership } from '@/lib/membership';
import { useSession } from '@/stores/session';

/** Authenticated layout: needs a session AND a selected shop, otherwise redirects. */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    try {
      const data = await context.queryClient.ensureQueryData(meQueryOptions);
      const membership = pickActiveMembership(data.memberships, useSession.getState().tenantId);
      if (!membership) throw redirect({ to: '/select-tenant', search: signInSearch(location.href) });
      useSession.getState().setTenant(membership.tenant.id);
    } catch (e) {
      if (isApiError(e) && e.status === 401) {
        const ended = impersonationEndedRedirect(e);
        if (ended) throw ended;
        throw redirect({ to: '/sign-in', search: signInSearch(location.href) });
      }
      throw e;
    }
  },
  component: AppLayout,
});

/** What a shop that must pay first can still open (the API keeps the same list open): billing, account, help. */
const LOCKED_OPEN = /^\/(billing|account|help)(\/|$)/;

function AppLayout() {
  const me = useMe();
  const pathname = useLocation({ select: (l) => l.pathname });
  const tenantId = useSession((s) => s.tenantId);
  if (!me.data) {
    return (
      <div className="grid gap-3 p-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  const membership = pickActiveMembership(me.data.memberships, tenantId);
  if (!membership) return <Navigate to="/select-tenant" search={{}} />;
  if (membership.tenant.subscription?.state === 'locked') {
    return (
      <ActingMembershipProvider membership={membership}>
        <LockedShell>{LOCKED_OPEN.test(pathname) ? <Outlet /> : <LockedScreen />}</LockedShell>
      </ActingMembershipProvider>
    );
  }
  return (
    <ActingMembershipProvider membership={membership}>
      <AppShell>
        <Outlet />
      </AppShell>
    </ActingMembershipProvider>
  );
}
